import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, ChevronDown, Lock } from 'lucide-react';
import '@/App.css';
import highlight_1 from '@/assets/highlight_1.jpeg';
import highlight_2 from '@/assets/highlight_2.jpeg';
import highlight_3 from '@/assets/highlight_3.jpeg';
import highlight_4 from '@/assets/highlight_4.jpeg';
import highlight_5 from '@/assets/highlight_5.jpeg';

import Navigation from '@/components/nav';
import HeroSection from '@/components/HeroSection';
import FooterSection from '@/components/footer';

const API_BASE = import.meta.env.VITE_API_BASE|| "https://qwaqwatalentshow.co.za";;

interface HighlightItem {
  id: number;
  title: string;
  date: string;
  description: string;
  thumbnail: string;
  attendance: number;
  category_id: string;
  featured: boolean;
}

// Gallery Popup Component
function GalleryPopup({ 
  isOpen, 
  onClose, 
  initialIndex, 
  items 
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  initialIndex: number; 
  items: HighlightItem[];
}) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % items.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
  };

  if (!isOpen) return null;

  const current = items[currentIndex];

  return createPortal(
    <div
      className="fixed inset-0 bg-black/90 flex items-center justify-center p-4"
      style={{ position: 'fixed', inset: 0, zIndex: 9999999999 }}
      onClick={onClose}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        className="max-w-5xl w-full max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-4 z-50">
          <div>
            <h2 className="text-3xl font-black text-white">{current.title}</h2>
            <p className="text-QWA2_TALENT_SHOW-gold text-sm mt-1">{current.date.split('-')[0]}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-QWA2_TALENT_SHOW-gold transition-colors"
          >
            <X className="w-8 h-8" />
          </button>
        </div>

        {/* Main Image */}
        <div className="relative flex-1 flex items-center justify-center mb-4 rounded-2xl overflow-hidden bg-black">
          <img
            src={current.thumbnail.startsWith('http') ? current.thumbnail : `${API_BASE}${current.thumbnail}`}
            alt={current.title}
            className="w-full h-full object-contain"
          />

          {/* Navigation Arrows */}
          <button
            onClick={prevImage}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-QWA2_TALENT_SHOW-gold/20 hover:bg-QWA2_TALENT_SHOW-gold/40 text-white p-3 rounded-full transition-all"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-QWA2_TALENT_SHOW-gold/20 hover:bg-QWA2_TALENT_SHOW-gold/40 text-white p-3 rounded-full transition-all"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>

        {/* Description and Counter */}
        <div className="flex justify-between items-end">
          <p className="text-white/80 text-lg">{current.description}</p>
          <p className="text-QWA2_TALENT_SHOW-gold font-semibold">
            {currentIndex + 1} / {items.length}
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Highlights Section
function HighlightsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [highlights, setHighlights] = useState<HighlightItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [expandedYear, setExpandedYear] = useState<string>('');

  useEffect(() => {
    const fetchHighlights = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/api/v1/highlights`);
        const data = await res.json();
        if (Array.isArray(data)) {
          setHighlights(data);
          const years = data.map(h => h.date.split('-')[0]);
          const latestYear = years.sort((a, b) => Number(b) - Number(a))[0];
          if (latestYear) setExpandedYear(latestYear);
        }
      } catch (err) {
        console.error("Failed to fetch highlights:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchHighlights();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const highlightsByYear = useMemo(() => {
    const grouped = highlights.reduce<Record<string, HighlightItem[]>>((acc, item) => {
      const year = item.date ? item.date.split('-')[0] : 'Unknown';
      if (!acc[year]) acc[year] = [];
      acc[year].push(item);
      return acc;
    }, {});
    return Object.entries(grouped).sort(([a], [b]) => Number(b) - Number(a));
  }, [highlights]);

  const handleImageClick = (highlight: HighlightItem) => {
    const index = highlights.findIndex((item) => item.id === highlight.id);
    if (index >= 0) {
      setSelectedIndex(index);
      setGalleryOpen(true);
    }
  };

  const toggleYear = (year: string) => {
    setExpandedYear((current) => (current === year ? '' : year));
  };

  return (
    <div id="highlights" ref={sectionRef} className="w-full relative overflow-hidden py-12">
      {/* Header Section */}
     <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
  <div
    className={`transition-all duration-700 ${
      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
    }`}
  >
    <h2 className="font-black text-4xl md:text-6xl text-QWA2_TALENT_SHOW-black mb-4">
      MOMENTS of <span className="red-gold-gradient-text">2026</span>
    </h2>

    <p className="text-QWA2_TALENT_SHOW-gray text-lg max-w-2xl mb-6">
      The 2026 highlights will be available once the competition concludes.
      Stay tuned for unforgettable moments.
    </p>

    {/* 🔥 LOADING / LOCKED STATE */}
    <div className="relative overflow-hidden rounded-2xl border border-QWA2_TALENT_SHOW-gold/20 bg-white/60 backdrop-blur-xl p-6 shadow-lg">

      {/* shimmer effect */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -left-1/2 top-0 h-full w-1/2 bg-white/40 blur-2xl animate-[shine_2s_linear_infinite]" />
      </div>

      <div className="relative z-10 flex flex-col items-center justify-center text-center space-y-4">

        {/* LOCK ICON */}
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-QWA2_TALENT_SHOW-gold to-QWA2_TALENT_SHOW-red flex items-center justify-center text-white text-xl shadow-md">
          <Lock className="w-6 h-6" />
        </div>

        {/* TEXT */}
        <h3 className="text-xl font-bold text-QWA2_TALENT_SHOW-black">
          Highlights Locked
        </h3>

        <p className="text-sm text-QWA2_TALENT_SHOW-gray max-w-md">
          The gallery will unlock after the 2026 competition ends. Come back soon to relive the best moments.
        </p>

        {/* LOADING DOTS */}
        <div className="flex gap-2 mt-2">
          <span className="w-2 h-2 bg-QWA2_TALENT_SHOW-gold rounded-full animate-bounce" />
          <span className="w-2 h-2 bg-QWA2_TALENT_SHOW-red rounded-full animate-bounce delay-150" />
          <span className="w-2 h-2 bg-QWA2_TALENT_SHOW-gold rounded-full animate-bounce delay-300" />
        </div>

      </div>
    </div>
  </div>
</div>

      {/* Background Accents */}
      <div className="absolute top-20 left-20 w-32 h-32 rounded-full bg-QWA2_TALENT_SHOW-gold/10 blur-3xl" />
      <div className="absolute bottom-20 right-20 w-40 h-40 rounded-full bg-QWA2_TALENT_SHOW-gold/10 blur-3xl" />

      {/* Year Sections */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        {highlightsByYear.map(([year, items]) => (
          <div key={year} className="rounded-3xl border border-QWA2_TALENT_SHOW-gold/20 bg-white/95 shadow-lg overflow-hidden">
            <button
              type="button"
              onClick={() => toggleYear(year)}
              className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
            >
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-QWA2_TALENT_SHOW-gold">{year}</p>
                <p className="mt-2 text-2xl font-black text-QWA2_TALENT_SHOW-black">{items.length} highlights</p>
              </div>
              <ChevronDown
                className={`h-6 w-6 text-QWA2_TALENT_SHOW-black transition-transform duration-300 ${expandedYear === year ? 'rotate-180' : ''}`}
              />
            </button>
            {expandedYear === year && (
              <div className="border-t border-QWA2_TALENT_SHOW-gold/10 px-4 py-6 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {items.map((highlight) => (
                    <div
              key={highlight.id}
              onClick={() => handleImageClick(highlight)}
              className={`group relative overflow-hidden rounded-xl cursor-pointer transition-all duration-500 hover:shadow-2xl hover:scale-105 border border-QWA2_TALENT_SHOW-gold/20 hover:border-QWA2_TALENT_SHOW-gold/60 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              } h-48 md:h-56 lg:h-64`}
              style={{ transitionDelay: `${highlight.id * 50}ms` }}
            >
              <img
                src={highlight.thumbnail.startsWith('http') ? highlight.thumbnail : `${API_BASE}${highlight.thumbnail}`}
                alt={highlight.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-125"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute inset-0 flex flex-col justify-between p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="text-right">
                  <span className="inline-block bg-QWA2_TALENT_SHOW-gold text-black text-xs font-bold px-3 py-1 rounded-full">
                    {highlight.date.split('-')[0]}
                  </span>
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg leading-tight">{highlight.title}</h3>
                  <p className="text-white/70 text-xs mt-2 line-clamp-2">{highlight.description}</p>
                </div>
              </div>
            </div>
                  ))}
                </div>
              </div>
            )}
          </div>






        ))}
      </div>

      {/* Gallery Modal */}
      <GalleryPopup isOpen={galleryOpen} onClose={() => setGalleryOpen(false)} initialIndex={selectedIndex} items={highlights} />
    </div>
  );
}

// Votes page
function Highlights() {

    return (
        <div className="min-h-screen bg-white">
            <HeroSection title='Best Moments' description='Hightlights' long='Relive the most unforgettable moments from the QwaQwa Talent Show.' />
            <Navigation />
            <main>
                <section id="vote" className="relative py-20 md:py-32 bg-gradient-to-b from-white to-gray-50 overflow-hidden">

                    {/* Background Glow Effects */}
                    <div className="absolute top-40 right-10 md:right-20 w-40 h-40 rounded-full bg-QWA2_TALENT_SHOW-gold/20 blur-3xl" />
                    <div className="absolute bottom-20 left-10 w-60 h-60 rounded-full bg-QWA2_TALENT_SHOW-red/10 blur-3xl" />

                    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                        





<HighlightsSection />



                    </div>

                </section>
                <FooterSection />
            </main>
        </div>
    );
}


export default Highlights;
